'use client';

import {
  VpFormControl,
  VpFormError,
  VpIcon,
  VpIconButton,
  VpTextarea,
} from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Loader } from '@/app/components/Loader';
import { isUserAdmin } from '~/components/Profile/utils';
import { isMainUser } from '~/utils/user/user';
import { useAuthenticatedContext } from '../Provider';
import { checkRequiredContent } from './utils';

export const ItemPost = ({
  forChildren,
  id,
  parentId,
  postComment,
  processing,
}: {
  forChildren?: boolean;
  id?: number | string;
  parentId?: number;
  processing: boolean;
  postComment: (
    as_admin: boolean,
    value: string,
    parent?: number,
  ) => Promise<void>;
}) => {
  const t = useTranslations();

  const { user } = useAuthenticatedContext();

  const [asAdmin, setAsAdmin] = useState(false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    checkRequiredContent(content);
    try {
      await postComment(asAdmin, content, parentId);
      setLoading(false);
      setContent('');
    } catch {
      setLoading(false);
    }
  };

  const onChangeSwitch = () => {
    setAsAdmin(current => !current);
  };

  return isMainUser(user)
    ? (
        <Loader loading={loading}>
          <section
            data-testid="post"
            className={`comment-post ${
              id === 'post-idea' ? 'comment-post--idea' : ''
            }`}
          >
            <form onSubmit={handleSubmit}>
              <VpFormControl
                required
                {...(content.length > 1999 && { status: 'error' })}
              >
                <div className="comment-post__form">
                  <div className="comment-post__form__textarea">
                    <VpTextarea
                      data-testid="textarea"
                      data-cy="answer"
                      name="answer"
                      aria-label={t('DISCUSSION.SHARE_VIEW.PLACEHOLDER')}
                      placeholder={t('DISCUSSION.SHARE_VIEW.PLACEHOLDER')}
                      value={content}
                      maxLength={2000}
                      onChange={event => setContent(event?.currentTarget?.value)}
                    />
                  </div>
                  <VpIconButton
                    aria-label={t('PROFILE.GLOBAL.VALIDATE')}
                    type="submit"
                    data-cy="submit"
                    data-testid="submit"
                    size="medium"
                    disabled={processing || !content}
                  >
                    <VpIcon name="send-plane" />
                  </VpIconButton>
                </div>
                {content.length > 1999 && (
                  <VpFormError>
                    {t('DISCUSSION.SHARE_VIEW.ERROR')}
                  </VpFormError>
                )}
              </VpFormControl>
            </form>
            {isUserAdmin(user) && !forChildren && (
              <div className="admin-signature">
                <div className="admin-signature__icon">
                  <VpIcon name="user" size={16} />
                  <VpIcon name="lock" size={16} />
                </div>
                <div className="admin-signature__switch">
                  <div className="vtmn-toggle vtmn-toggle_size--small">
                    <div className="vtmn-toggle_switch">
                      <input
                        type="checkbox"
                        id={`switch-admin-${id}`}
                        data-testid="admin-switch"
                        checked={asAdmin}
                        onChange={onChangeSwitch}
                      />
                      <span aria-hidden="true"></span>
                    </div>
                  </div>
                </div>
                <label
                  htmlFor={`switch-admin-${id}`}
                  className="admin-signature__content"
                >
                  {t('DISCUSSION.ADMIN_SIGNATURE')}
                  {' '}
                  <span className="admin-signature__identity">
                    {user && `"${user.firstname}, ${user.label ? user.label : user.job}"`}
                  </span>
                </label>
              </div>
            )}
          </section>
        </Loader>
      )
    : null;
};
